import { useWbsItemSearchConditionV2SerializeService } from '../../transform-service/wbsItemSearchConditionV2SerializeService'
import { useCallback, useMemo } from 'react'
import { WbsItemSearchConditionV2VO } from '../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { BulkSheetColumnState } from '../../../view/model/bulkSheetColumnAndFilterState'
import { PermanentUiState } from '../../model/uiState'
import {
  SavePermanentUiStateRequest,
  usePermanentUiStateStorageService,
} from './permanentUiStateStorageService'
import { useWbsItemSearchConditionV2Factory } from '../../factories/wbsItemSearchConditionV2Factory'

export type SearchConditionAndColumnStateV2 = {
  searchCondition: WbsItemSearchConditionV2VO
  columnState: BulkSheetColumnState
}

export type SearchConditionAndColumnStateV2StorageService = {
  stored: PermanentUiState<SearchConditionAndColumnStateV2>[]
  fetch: () => Promise<void>
  save: (
    request: SavePermanentUiStateRequest<SearchConditionAndColumnStateV2>
  ) => Promise<void>
  delete: (uuid: string) => Promise<void>
  getByCode: (
    code: string
  ) => Promise<PermanentUiState<SearchConditionAndColumnStateV2> | undefined>
}

export const useSearchConditionAndColumnStateV2StorageService = (
  functionUuid: string,
  projectUuid: string
): SearchConditionAndColumnStateV2StorageService => {
  const uiStateKey = 'BULK_SHEET_UI_STATE_SEARCH_CONDITION'

  const permanentUiStateStorageService = usePermanentUiStateStorageService(
    functionUuid,
    uiStateKey,
    projectUuid
  )

  const searchConditionSerializeService =
    useWbsItemSearchConditionV2SerializeService()
  const searchConditionFactory = useWbsItemSearchConditionV2Factory()

  const stored = useMemo(() => {
    return permanentUiStateStorageService.stored.map(s => {
      const storedState = s.value
      return {
        ...s,
        value: {
          searchCondition: searchConditionFactory.fromApiResponseToVo(
            s.value.searchCondition
          ),
          columnState: storedState.columnState,
        },
      }
    })
  }, [permanentUiStateStorageService.stored])

  const fetch = useMemo(
    () => permanentUiStateStorageService.fetch,
    [permanentUiStateStorageService.fetch]
  )

  const save = useCallback(
    async (
      request: SavePermanentUiStateRequest<SearchConditionAndColumnStateV2>
    ) => {
      const value = request.value
      const searchCondition =
        searchConditionSerializeService.fromVoToStoredObject(
          value.searchCondition
        )
      const plainValue = {
        searchCondition,
        columnState: value.columnState,
      }
      const deserializedRequest = {
        ...request,
        value: plainValue,
      }
      permanentUiStateStorageService.save(deserializedRequest)
    },
    [permanentUiStateStorageService.save]
  )

  const deleteState = useMemo(
    () => permanentUiStateStorageService.delete,
    [permanentUiStateStorageService.delete]
  )

  const getByCode = useCallback(
    async (code: string) => {
      const response = await permanentUiStateStorageService.getByCode(code)
      if (!response) return undefined
      const storedState = response.value
      return {
        ...response,
        value: {
          searchCondition: searchConditionFactory.fromApiResponseToVo(
            storedState.searchCondition
          ),
          columnState: storedState.columnState,
        },
      }
    },
    [permanentUiStateStorageService.getByCode]
  )

  return useMemo(
    () => ({
      stored,
      fetch,
      save,
      delete: deleteState,
      getByCode,
    }),
    [stored, fetch, save, deleteState, getByCode]
  )
}
